import React from 'react';
import Dropdown from '../Dropdown';
import CSearchbar from './c-searchbar';
import CMultiSelect from './c-multi-select';

/**
 * CFiltersBar - shared, reusable filter bar.
 *
 * Reproduces the standard list-page filter UI: an optional search field followed
 * by a sequence of labeled custom Dropdowns and a trailing "Clear Filters"
 * button, laid out on a responsive 4-column grid.
 *
 * Fully data-driven via the `filters` array so it can serve any list page
 * (Employees, Appraisals, Documents, Leaves, Salaries, ...) - no page-specific
 * options are hardcoded here.
 *
 * @param {object}   props
 * @param {boolean}  [props.showSearch=true]       Render the search field.
 * @param {string}   [props.searchTerm]            Controlled search input value.
 * @param {Function} [props.onSearchChange]        Search input onChange handler.
 * @param {string}   [props.searchPlaceholder]     Search input placeholder text.
 * @param {boolean}  [props.searchLoading]         Show the loading spinner.
 * @param {string[]} [props.searchSuggestions]     Autocomplete entries for the search
 *   field (see CSearchbar); omit for a plain search field.
 * @param {Function} [props.onSearchSuggestionSelect] Called with the picked entry.
 * @param {Array}    [props.filters=[]]            Filter descriptors, rendered in order.
 *   Each item: { key?, label, value, onChange, options: [{ value, label }], testId? }
 *   `testId` is forwarded to that filter's Dropdown.
 *   With `multiple: true` the filter is a CMultiSelect instead: `value` is an
 *   array, `onChange` receives the next array, and `placeholder` is the text
 *   shown when nothing is picked.
 * @param {Function} [props.onClear]               Clear Filters button handler.
 * @param {string}   [props.searchTestId]          data-testid for the search input.
 * @param {string}   [props.clearTestId]           data-testid for the Clear Filters button.
 * @param {string}   [props.gridColsClass='md:grid-cols-4'] Responsive grid-columns class,
 *   so pages can size the grid to their filter count (e.g. 'md:grid-cols-5').
 */
const slug = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const CFiltersBar = ({
  showSearch = true,
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchLoading,
  searchSuggestions,
  onSearchSuggestionSelect,
  filters = [],
  onClear,
  searchTestId,
  clearTestId,
  gridColsClass = 'md:grid-cols-4',
}) => {
  return (
    <form
      className="bg-white p-4 rounded-lg shadow-soft"
      onSubmit={(e) => e.preventDefault()}
    >
      <div className={`grid grid-cols-1 ${gridColsClass} gap-4`}>
        {showSearch && (
          <CSearchbar
            value={searchTerm}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
            loading={searchLoading}
            suggestions={searchSuggestions}
            onSuggestionSelect={onSearchSuggestionSelect}
            data-testid={searchTestId}
            id={searchTestId ? `${searchTestId}-input` : undefined}
          />
        )}

        {filters.map((filter, idx) => {
          // Dropdown renders a <button aria-haspopup="listbox">, and a button's
          // accessible name comes from aria-labelledby / aria-label / its own
          // contents -- an associated <label> element is not in that chain. So
          // aria-label is what actually names the field; htmlFor/id only buys
          // click-to-focus.
          const fieldId = `filter-${filter.key || slug(filter.label) || idx}`;
          return (
          <div key={filter.key || filter.label || idx}>
            <label
              htmlFor={fieldId}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {filter.label}
            </label>
            {filter.multiple ? (
              <CMultiSelect
                id={fieldId}
                aria-label={filter.label}
                value={filter.value}
                onChange={filter.onChange}
                options={filter.options}
                placeholder={filter.placeholder}
                data-testid={filter.testId}
              />
            ) : (
              <Dropdown
                id={fieldId}
                aria-label={filter.label}
                value={filter.value}
                onChange={filter.onChange}
                data-testid={filter.testId}
              >
                {filter.options?.map((option, optionIdx) => (
                  <option
                    key={`${option.value}-${optionIdx}`}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </Dropdown>
            )}
          </div>
          );
        })}

        <div className="flex items-end">
          {/* The transparent border matches the fields' 1px one, so the button
              is exactly as tall as they are. */}
          <button
            type="button"
            data-testid={clearTestId}
            onClick={onClear}
            className="w-full border border-transparent bg-gray-100 text-gray-700 px-4 py-2 text-sm rounded-md hover:bg-gray-200 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </form>
  );
};

export default CFiltersBar;
